import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PlanningGridConfig } from '@core/config/planning-grid-config';
import { AllyaUserSettings } from '@core/enums/user-settings.enum';
import { UserSettingsRepository } from '@portal-shared/app/databases/repositories/user-settings.repository';
import { Period } from '@portal-shared/app/entities/period.model';
import { WindowHelper } from '@portal-shared/app/helpers/window.helper';
import { Dictionary } from '@portal-shared/app/typings/dictionary.type';
import { AllyaRouterHelper } from '@shared/helpers/router.helper';
import { ReplaySubject, combineLatest, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PeriodService } from './period.service';

@Injectable({
  providedIn: 'root'
})
export class PlanningBoardPeriodService {
  private visiblePeriods$ = new ReplaySubject<Period[]>(1);

  private _periodColumnsCount = PlanningGridConfig.columnConfigs.testPeriodColumnCount;

  private periodColumnChanged$ = new ReplaySubject<number>(1);
  public get periodColumnsCount() {
    const columnConfigs = PlanningGridConfig.columnConfigs;
    return this._periodColumnsCount ?? columnConfigs.testPeriodColumnCount;
  }

  public constructor(
    private periodService: PeriodService,
    private activeRoute: ActivatedRoute,
    private router: Router
  ) {}

  public async setPeriodColumnsCount() {
    if (environment.build === 'test' || WindowHelper.isCypress) {
      this._periodColumnsCount = PlanningGridConfig.columnConfigs.testPeriodColumnCount; // always 6 columns in test
    } else {
      const userDefinedPeriodsCount = await PlanningBoardPeriodService.getColumnCountBasedOnUserSetting();
      this._periodColumnsCount =
        userDefinedPeriodsCount ?? PlanningBoardPeriodService.getColumnCountBasedOnScreenSize();
    }

    this.periodColumnChanged$.next(this._periodColumnsCount);
  }

  private static getColumnCountBasedOnScreenSize() {
    const columnConfigs = PlanningGridConfig.columnConfigs;
    const periodColumnsWidth = screen.width - columnConfigs.groupColumnWidth; //remove resource column
    const columnCount = Math.floor(periodColumnsWidth / columnConfigs.periodColumnNormalWidth);
    return Math.max(columnConfigs.minPeriodColumnCount, columnCount);
  }

  private static async getColumnCountBasedOnUserSetting() {
    return UserSettingsRepository.getUserSetting<number>(AllyaUserSettings.numberOfPeriodsKey);
  }

  public observeVisiblePeriods() {
    return this.visiblePeriods$.asObservable();
  }

  public getVisiblePeriods() {
    return firstValueFrom(this.visiblePeriods$.asObservable());
  }

  public manageVisiblePeriods() {
    return combineLatest({
      periodIdStr: AllyaRouterHelper.observePeriodId(this.activeRoute),
      numberOfPeriods: this.periodColumnChanged$.asObservable(),
      periodEvents: this.periodService.observePeriodEvents()
    }).subscribe(async ({ periodIdStr, numberOfPeriods }) => {
      let periodId = Number(periodIdStr === '' ? undefined : periodIdStr);

      if (Number.isNaN(periodId)) {
        periodId = (await this.periodService.getCurrentPeriod()).id;
      }

      const from = periodId;
      const to = from + (numberOfPeriods - 1);

      const visiblePeriods = await this.periodService.getPeriodRange(from, to);

      this.visiblePeriods$.next(visiblePeriods);
      return visiblePeriods;
    }); // todo manageSubscription
  }

  public async navigateTo(periodId?: number) {
    const query: Dictionary<string, string> = {};

    query[AllyaRouterHelper.periodIdParam] = periodId?.toString() ?? '';

    await AllyaRouterHelper.setQueryParam(this.router, this.activeRoute, query);
  }

  public async navigateBy(amount: number) {
    const visiblePeriods = await this.getVisiblePeriods();

    if (visiblePeriods.length === 0) {
      throw new Error("Can't navigateBy when there are no visible periods");
    }

    await this.navigateTo(visiblePeriods[0].id + amount);
  }
}
